import { Controller, SpringValue } from '@react-spring/web'
import { useFrame, useLoader, useThree } from '@react-three/fiber'
import type { GroupProps } from '@react-three/fiber'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import type { Group, MeshStandardMaterial, Texture } from 'three'
import { Color, ImageLoader } from 'three'

import { preloadGLTF, useGLTFLoader } from '../../lib/utils/useGLTFLoader'
import { colors } from '../token-icon/constants'

const GLTF_LOCATION = '/3d/card-compressed.glb'

export const Card = forwardRef<Group, CardProps>(
    ({ color = 'future-fuscia', onLoad, ...props }, ref) => {
        const {
            invalidate,
            performance: { regress },
        } = useThree()
        const sidesRef = useRef<MeshStandardMaterial | null>(null)
        const colorSpring = useRef<Controller | null>(null)
        const cardRef = useRef<Group | null>(null)
        const textureLoaded = useRef(false)
        const meshLoadedRef = useRef(false)
        const [frontMap, backMap] = useLoader(ImageLoader, [
            '/assets/homepage/card-front-small.png',
            '/assets/homepage/card-back-small.png',
        ])
        const loadingSpring = useMemo(
            () =>
                new SpringValue(0, {
                    config: { tension: 120 },
                    onChange: () => invalidate(),
                }),
            [invalidate]
        )
        const { nodes, materials } = useGLTFLoader(GLTF_LOCATION)

        const handleLoad = useCallback(() => {
            regress()
            if (textureLoaded.current && meshLoadedRef.current) {
                // loadingSpring.start(1)
                if (onLoad) onLoad()
            }
        }, [regress])

        const setRef = useCallback(
            (obj: Group) => {
                if (ref && 'current' in ref) {
                    ref.current = obj
                }
                meshLoadedRef.current = true
                handleLoad()
            },
            [handleLoad]
        )

        const updateLoadAnimation = useCallback(() => {
            if (cardRef.current) {
                const loadState = loadingSpring.get()
                cardRef.current.position.y = (loadState - 1) / 4
            }
        }, [])

        const updateColor = useCallback(() => {
            if (colorSpring.current && sidesRef.current) {
                const currentColor = colorSpring.current.get()
                sidesRef.current.color.setRGB(
                    currentColor.r,
                    currentColor.g,
                    currentColor.b
                )
            }
        }, [])

        const handleTextureLoad = useCallback(() => {
            textureLoaded.current = true
            handleLoad()
        }, [handleLoad])

        useFrame(() => {
            if (!loadingSpring.idle) updateLoadAnimation()
            if (colorSpring.current && !colorSpring.current.idle) updateColor()
        })

        useEffect(() => {
            if (sidesRef.current) {
                const inputColor = new Color(colors[color])
                if (!colorSpring.current) {
                    colorSpring.current = new Controller({
                        ...inputColor,
                        onChange: () => invalidate(),
                    })
                    updateColor()
                } else colorSpring.current.start(inputColor)
            }
        }, [color, invalidate])

        useEffect(() => {
            if (frontMap && backMap) {
                const frontTexture = (materials.front as MeshStandardMaterial)
                    .map as Texture
                const backTexture = (materials.back as MeshStandardMaterial)
                    .map as Texture
                frontTexture.image = frontMap
                backTexture.image = backMap
                handleTextureLoad()
            }
        }, [frontMap, backMap])

        return (
            <group ref={cardRef} dispose={null}>
                <group ref={setRef} {...props}>
                    <group
                        scale={0.48}
                        rotation={[Math.PI / 2, -Math.PI / 2, Math.PI / 2]}
                    >
                        <mesh
                            geometry={nodes['01_step_card_front'].geometry}
                            material={materials.front}
                        />
                        <mesh
                            geometry={nodes['03_step_card_back'].geometry}
                            material={materials.back}
                        />
                        <mesh geometry={nodes['02_step_card_side'].geometry}>
                            <meshStandardMaterial
                                ref={sidesRef}
                                attach='material'
                            />
                        </mesh>
                    </group>
                </group>
            </group>
        )
    }
)

preloadGLTF(GLTF_LOCATION)

interface CardProps extends GroupProps {
    color?: string
    onLoad?: () => void
}
